import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../LangContext";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/b9279398-bca1-4726-a256-3cb06bf2f4ad/images/97b1c43c75292b322d45740f5b855930d32cdcbc8dddea42eb874329f672a782.png";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay },
});

export default function Hero() {
  const { t } = useLang();

  const goTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden"
    >
      {/* Subtle hero texture */}
      <div
        aria-hidden
        className="absolute inset-0 -z-0 opacity-[0.18] pointer-events-none"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          maskImage:
            "radial-gradient(80% 60% at 70% 40%, rgba(0,0,0,0.9), rgba(0,0,0,0))",
          WebkitMaskImage:
            "radial-gradient(80% 60% at 70% 40%, rgba(0,0,0,0.9), rgba(0,0,0,0))",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          {...fade(0)}
          className="eyebrow text-ink2 mb-10"
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.p>

        <div className="grid grid-cols-12 gap-y-10">
          <div className="col-span-12 lg:col-span-9">
            <motion.h1
              {...fade(0.05)}
              className="headline text-ink text-[44px] sm:text-6xl lg:text-[112px] leading-[0.98]"
              data-testid="hero-headline"
            >
              {t.hero.headline_a}
              <br />
              <span className="headline-italic text-ink2">{t.hero.headline_b}</span>
              <br />
              {t.hero.headline_c}
            </motion.h1>
          </div>

          <div className="col-span-12 lg:col-span-7 lg:col-start-6 mt-8 lg:mt-14">
            <motion.p
              {...fade(0.15)}
              className="text-base sm:text-lg text-ink2 leading-relaxed max-w-xl"
              data-testid="hero-lede"
            >
              {t.hero.lede}
            </motion.p>

            <motion.div
              {...fade(0.25)}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href="#contact"
                onClick={goTo("contact")}
                data-testid="hero-primary-cta"
                className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-ink text-cream text-sm font-medium hover:bg-ink2 transition-all duration-300"
              >
                {t.hero.cta_primary} <span>→</span>
              </a>
              <a
                href="#services"
                onClick={goTo("services")}
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-3 px-7 py-4 rounded-full border border-ink text-ink text-sm font-medium hover:bg-ink hover:text-cream transition-all duration-300"
              >
                {t.hero.cta_secondary}
              </a>
            </motion.div>
          </div>
        </div>

        <motion.div
          {...fade(0.4)}
          className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 border-t border-line"
          data-testid="hero-stats"
        >
          {[t.hero.stat_a, t.hero.stat_b, t.hero.stat_c].map((s, i) => (
            <div
              key={i}
              className={`py-7 md:px-8 ${i > 0 ? "md:border-l border-line" : ""} ${
                i > 0 ? "border-t md:border-t-0" : ""
              } border-line`}
            >
              <p className="font-serif text-2xl md:text-3xl text-ink leading-tight">
                {s}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
